import { useToast } from 'vue-toastification';
import type { ToastOptions } from 'vue-toastification/dist/types/types';
import { isServiceError } from '@core/models/actionResponse';
import type { ServiceResult, ServiceError } from '@core/models/actionResponse';
import type { ServerError } from '../models/base';
import { isServerError } from '../models/base';

export type ToastifyOptions = Omit<ToastOptions, 'type'>;
export type ToastMessage = string | (() => string) | null | undefined;

export const useToaster = () => {
  const { success, error, info, warning } = useToast();

  const toaster = async <T>(
    fn: () => Promise<ServiceResult<T>>,
    successMsg: (() => string) | string,
    errorMsg: ((e: Error | ServerError) => string) | string = (e: Error) => e.message.toString(),
  ) => {
    let result: Awaited<T> | ServiceResult<T> | undefined = undefined;
    try {
      result = await fn();
      if (result && isServiceError(result)) {
        throw result.errors[0];
      }

      if (successMsg) {
        success(successMsg instanceof Function ? successMsg() : successMsg);
      }
    } catch (e) {
      if (errorMsg instanceof Function && (e instanceof Error || isServerError(e))) {
        error(errorMsg(e));
      } else {
        error(errorMsg);
      }
    }
    return result;
  };

  const toastify = (
    results: ServiceResult<unknown> | ServiceResult<unknown>[],
    successMsg?: ToastMessage,
    errorMsg?: ToastMessage,
    errOptions?: ToastifyOptions,
  ) => {
    const toProcess = Array.isArray(results) ? results : [results];
    const errorResults = toProcess.filter((e) => isServiceError(e)).map((e) => e as ServiceError);
    if (errorResults.length) {
      const errorMessages = errorResults.map((e) => e.errors).flat();
      // errorMessages.forEach((e) => error(e));
      error(
        errorMsg ? errorMsg : errorMessages.length > 1 ? `Oops:\r\n${errorMessages.join('\r\n')}` : errorMessages[0],
        errOptions,
      );
    } else if (successMsg) {
      success(successMsg instanceof Function ? successMsg() : successMsg);
    }
  };

  const simpleToast = (text: string, type: 'success' | 'error' | 'info' | 'warning') => {
    switch (type) {
      case 'success':
        success(text);
        break;
      case 'error':
        error(text);
        break;
      case 'info':
        info(text);
        break;
      case 'warning':
        warning(text);
        break;
    }
  };

  return {
    toaster,
    toastify,
    simpleToast,
  };
};
